<template>
  <div
    :class="[
      'relative bg-primary-900 dark:bg-gray-900 h-screen w-screen sm:overflow-hidden text-gray-900 dark:text-white',
      $auth.theme.light.gradient ? `${$auth.theme.light.gradient} dark:bg-gray-900` : '',
    ]"
  >
    <incidents class="z-10 relative" />

    <div
      :class="[
        'inset-0 h-full w-full bg-cover bg-center absolute bg-no-repeat',
        $auth.theme.light.gradient ? `bg-black/5 dark:bg-gray-950/10` : `bg-primary-950/20 dark:bg-gray-950/10`,
      ]"
      :style="mask"
    />

    <slot v-if="!slug || location" />

    <div v-if="error" class="h-screen w-screen fixed inset-0 bg-gray-50 flex items-center z-80000">
      <div class="mx-auto max-w-md px-5 text-gray-700 text-center">
        <div class="text-6xl font-dark font-bold leading-none mb-6">
          {{ 'Something went wrong' }}
        </div>
        <p class="text-2xl tracking-tighter font-light leading-normal">
          {{ 'Sorry, it looks like theres been error or the requested account doesn\'t exist...' }}
        </p>
        <p class="mb-8 mt-8">
          Contact hello@ovatu.com if you need some help
        </p>

        <o-button to="/auth/find/domain" size="lg" variant="info">
          Back to safety
        </o-button>
      </div>
    </div>

    <maintenance />
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '~/stores/useAuthStore'
import { useReactiveTheme } from '~/composables/useTheme'
// @ts-ignore
import Incidents from '@/components/layout/Incidents.vue'
// @ts-ignore
import Maintenance from '@/components/layout/Maintenance.vue'

const { $auth, $api } = useNuxtApp()
const route = useRoute()
const authStore = useAuthStore()

useReactiveTheme(
  () => ($auth as any).theme,
  () => ($auth as any).info?.location?.title,
)

const error = ref(false)
const location = ref<any>(null)

const slug = computed(() => ($auth as any).location)

const mask = computed(() => {
  const imgUrl = '/images/illustrations/illustration-green.webp'

  return {
    '-webkit-mask-image': `url('${imgUrl}')`,
    'mask-image': `url('${imgUrl}')`,
    'webkit-mask-position': 'center',
    'mask-position': 'center',
    '-webkit-mask-size': 'cover',
    'mask-size': 'cover',
  }
})

const fetchDomain = async () => {
  try {
    const domain = slug.value

    if (domain) {
      const response = await $api.auth.domain(domain)

      if (response) {
        location.value = response

        document.title = location.value.title + ' - Ovatu Manager'
      }
    }
  } catch (err) {
    error.value = true
    console.log(err)
  }
}

onMounted(() => {
  fetchDomain()

  const redirect = route.query.redirect

  if (redirect) {
    authStore.setAuthField({ key: 'redirect', value: redirect })
  }
})

provide('location', location)
</script>

<style lang="scss">
  $gradient-2: rgb(0, 154, 255);
  $gradient-1: rgb(24, 198, 82);
  $gradient-3: rgb(68, 93, 140);
  $gradient-4: yellow;

  .gradient-background {
    background: {
      image:
        radial-gradient(rgba($gradient-1, 0.5), rgba($gradient-1, 0) 40vw),
        radial-gradient(rgba($gradient-2, 0.3), rgba($gradient-2, 0) 40vw),
        radial-gradient(rgba($gradient-3, 0.5), rgba($gradient-3, 0) 40vw);
      position:
        -40vw 14rem,
        50% 10rem,
        60vw 14rem,
        -10vw calc(14rem + 20vw),
        30vw calc(14rem + 20vw);
      size: 80vw 80vw;
      repeat: no-repeat;
    }
  }
</style>
